import {
  Component,
  OnInit,
  Input,
  ViewChild,
  AfterViewInit,
  ElementRef,
  HostBinding,
  OnDestroy,
  Optional,
  Self,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  DoCheck,
  Output,
  EventEmitter,
  ContentChild,
  TemplateRef,
} from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { TranslateService } from '@ngx-translate/core';
import { no } from './i18n/no';
import { en } from './i18n/en';
import { Subject, merge, fromEvent } from 'rxjs';
import { UntypedFormControl, ControlValueAccessor, NgControl } from '@angular/forms';
import { MatLegacyFormFieldControl as MatFormFieldControl } from '@angular/material/legacy-form-field';
import { takeUntil, startWith, filter } from 'rxjs/operators';
import { MwSelectFooterDirective } from './select.directives';

@Component({
  selector: 'mw-select',
  templateUrl: 'select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: MwSelectComponent,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
}) // extends CustomComponentBase
export class MwSelectComponent
  implements
    OnInit,
    OnDestroy,
    DoCheck,
    AfterViewInit,
    ControlValueAccessor,
    MatFormFieldControl<any>
{
  static nextId = 0;

  @HostBinding() id = `mw-select-${MwSelectComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedBy = '';

  @ViewChild('searchInputElement')
  searchInputElement: ElementRef;
  @ViewChild('select') select: NgSelectComponent;

  @ContentChild(MwSelectFooterDirective, {
    read: TemplateRef,
  })
  footerTemplate: TemplateRef<any>;

  @Output() openDropDown = new EventEmitter();
  @Output() closeDropDown = new EventEmitter();
  @Output() search = new EventEmitter<{ term: string; items: any[] }>();
  @Output() clear = new EventEmitter();
  @Output() add = new EventEmitter();
  @Output() remove = new EventEmitter();
  @Output() scrollPanel = new EventEmitter<{ start: number; end: number }>();
  @Output() scrollToEnd = new EventEmitter();
  @Output() changeSelection = new EventEmitter();

  @Input() bindLabel: string;
  @Input() bindCaptionLabel: string;
  @Input() bindValue: string;
  @Input() bindClassName: string;
  @Input() groupBy: string;
  @Input() multiple: boolean;
  @Input() selectableGroup: boolean;
  @Input() searchable = true;
  @Input() loading = false;
  @Input() minTermLength: number;
  @Input() typeToSearchText: string;
  @Input() typeahead: Subject<string>;
  @Input() appendTo: string;
  @Input() readonly = false;
  @Input() allowSelectAll = true;
  @Input() clearable = true;
  @Input() autoCompleteMode = false;

  @Input()
  set items(value: any[]) {
    this.localItems = value || [];
  }

  get items(): any[] {
    return this.localItems;
  }

  @Input()
  set placeholder(value: string) {
    this.localPlaceholder = value;
    this.stateChanges.next();
  }

  @Input() set value(value: any) {
    this.formControl.setValue(value);
  }
  @Input()
  set required(value) {
    this.localRequired = value;
    this.stateChanges.next();
  }

  @Input()
  get disabled(): boolean {
    return this.localDisabled;
  }
  set disabled(value: boolean) {
    this.localDisabled = value === true;

    if (this.select) {
      this.select.setDisabledState(this.localDisabled);
    }
    this.stateChanges.next();
  }

  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  get required(): boolean {
    return this.localRequired;
  }

  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  get placeholder(): string {
    return this.localPlaceholder;
  }

  // eslint-disable-next-line @typescript-eslint/adjacent-overload-signatures
  get value(): any {
    return this.formControl.value;
  }

  get focused(): boolean {
    return this.localFocused || (this.select && this.select.isOpen) || false;
  }

  get empty(): boolean {
    return this.select && this.select.hasValue === false;
  }

  get errorState(): boolean {
    return this.localErrorState;
  }

  get indeterminateSelectAll(): boolean {
    const selectedItems = this.getSelectedItems();
    const allItems = this.items.filter((t) => t.disabled !== true);
    return selectedItems.length > 0 && allItems.length !== selectedItems.length;
  }

  get selectedAll(): boolean {
    const selectedItems = this.getSelectedItems();
    const allItems = this.items.filter((t) => t.disabled !== true);
    return (
      this.multiple &&
      selectedItems.length > 0 &&
      allItems.length === selectedItems.length
    );
  }

  get hasValue(): boolean {
    const value = this.formControl.value;

    return value && (value.length || String(value).length > 0);
  }

  shouldLabelFloat = true;
  searchInput: string;
  controlType = 'mw-select';
  stateChanges = new Subject<void>();
  formControl = new UntypedFormControl();

  private unsubscribeAll$ = new Subject<void>();
  private localPlaceholder: string;
  private localRequired = false;
  private localDisabled = false;
  private localFocused = false;
  private localErrorState = false;
  private localItems: any[] = [];
  private localOnChange = (_: any): void => {};
  private localOnTouched = (): void => {};

  constructor(
    private translateService: TranslateService,
    private changeDetectorRef: ChangeDetectorRef,
    private elementRef: ElementRef,
    @Optional() @Self() public ngControl: NgControl
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  isSelected(item: any): boolean {
    return this.getSelectedItems().indexOf(item[this.bindValue]) !== -1;
  }

  isGroupSelected(groupItem: any): boolean {
    const groupSelectedItemsValues =
      this.getGroupSelectedItemsValues(groupItem);
    const groupItemsValues = this.getGroupItemsValues(groupItem);

    return groupSelectedItemsValues.length === groupItemsValues.length;
  }

  isIndeterminateGroup(groupItem: any): boolean {
    const groupSelectedItemsValues =
      this.getGroupSelectedItemsValues(groupItem);
    const groupItemsValues = this.getGroupItemsValues(groupItem);

    return (
      groupSelectedItemsValues.length > 0 &&
      groupSelectedItemsValues.length !== groupItemsValues.length
    );
  }

  ngOnInit(): void {
    this.subscribeToLanguageChange();
    this.setTranslations(this.translateService.currentLang);

    if (document) {
      merge(
        fromEvent(document, 'touchstart', { capture: true }),
        fromEvent(document, 'mousedown', { capture: true })
      )
        .pipe(takeUntil(this.unsubscribeAll$))
        .subscribe((event) => this.onOutsideClick(event));
    }
  }

  ngOnDestroy(): void {
    this.stateChanges.complete();
    this.unsubscribeAll$.next();
    this.unsubscribeAll$.complete();
  }

  ngAfterViewInit(): void {
    if (this.searchable === false) {
      this.select.headerTemplate = null;
    }

    if (this.autoCompleteMode && !this.multiple) {
      this.formControl.valueChanges
        .pipe(
          startWith(this.formControl.value),
          filter((value) => !!value && !!this.select)
        )
        .subscribe((value) => {
          if (!this.select.selectedItems.find((t) => t.value === value)) {
            this.selectValue(value);
          }
        });
    }

    this.formControl.valueChanges.subscribe((value) => {
      if (this.localOnChange) {
        this.localOnChange(value);
      }

      this.stateChanges.next();
      this.changeSelection.emit(value);
    });
    this.select.setDisabledState(this.disabled || false);
    this.select.readonly = this.readonly;
    this.select.focusEvent.subscribe(() => (this.localFocused = true));
    this.select.blurEvent.subscribe(() => (this.localFocused = false));
    this.changeDetectorRef.detectChanges();
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.localErrorState = this.ngControl.invalid && this.ngControl.touched;
      this.formControl.setErrors(this.ngControl.errors);

      if (this.ngControl.touched) {
        this.formControl.markAsTouched();
      }
    }
    this.stateChanges.next();
  }

  onOutsideClick(event: any): void {
    if (this.elementRef.nativeElement.contains(event.target)) {
      return;
    }

    const path = event.path || (event.composedPath && event.composedPath());
    if (
      event.target &&
      event.target.shadowRoot &&
      path &&
      path[0] &&
      this.elementRef.nativeElement.contains(path[0])
    ) {
      return;
    }

    if (this.localFocused) {
      this.localOnTouched();
    }
    this.localFocused = false;
    this.stateChanges.next();
  }

  onDropDownOpen(): void {
    this.searchInput = '';
    this.localFocused = true;
    if (this.multiple === false && this.select.dropdownPanel) {
      this.select.dropdownPanel.scrollToTag();
    }

    const classes = [];

    if (this.appendTo) {
      classes.push('mw-select__drop-down--before-open');
    }

    if (this.searchable) {
      classes.push('mw-select__drop-down--searchable');
    }

    this.select.classes = classes.join(' ');
    this.changeDetectorRef.markForCheck();

    setTimeout(() => {
      if (this.searchInputElement) {
        this.searchInputElement.nativeElement.focus();
      }
    });

    this.openDropDown.emit();
  }

  onDropDownClose(): void {
    this.localOnTouched();
    this.select.focus();
    this.closeDropDown.emit();
  }

  onCheckboxChange(event: MatCheckboxChange, item: any): void {
    let selectedItems: any[] = [];

    if (event.checked) {
      selectedItems = [...this.getSelectedItems(), item[this.bindValue]];
    } else {
      selectedItems = this.getSelectedItems().filter(
        (t) => t !== item[this.bindValue]
      );
    }

    this.formControl.setValue(selectedItems);
  }

  onGroupCheckboxChange(event: MatCheckboxChange, groupItem: any): void {
    const groupItemValues = this.getGroupItemsValues(groupItem);
    const selectedItemsNotFromGroup = this.getSelectedItems().filter(
      (t) => groupItemValues.indexOf(t) === -1
    );
    let selectedItems: any[] = [];

    if (event.checked) {
      selectedItems = [...selectedItemsNotFromGroup, ...groupItemValues];
    } else {
      selectedItems = selectedItemsNotFromGroup;
    }

    this.formControl.setValue(selectedItems);
  }

  onSelectAll(event: MatCheckboxChange): void {
    let selectedItems: any[] = [];

    if (event.checked) {
      selectedItems = this.select.viewPortItems
        .map((t) => t.value)
        .filter(
          (t: { disabled: boolean }) => !(t instanceof Object) || !t.disabled
        )
        .map((t) => t[this.bindValue])
        .filter((value) => !!value);
    } else {
      selectedItems = [];
    }
    this.formControl.setValue(selectedItems);
  }

  onFilter(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    this.select.filter(inputEl.value);
    this.search.emit();
  }

  onClearSearchInput(): void {
    this.searchInput = '';
    this.select.filter('');

    if (this.typeahead) {
      this.typeahead.next('');
    }
  }

  onKeyDownFn(): ($event: KeyboardEvent) => boolean {
    return ($event: KeyboardEvent) => {
      if ($event.key === 'Enter' && this.autoCompleteMode && !this.multiple) {
        return false;
      }
    };
  }

  onSearchFieldEnterKeyDown($event: Event): void {
    if (this.autoCompleteMode && !this.multiple) {
      this.selectValue(this.searchInput);
      $event.stopImmediatePropagation();
    }
  }

  private selectValue(value: string): void {
    const item = this.select.itemsList.mapItem(value, null);
    this.select.select(item);
  }

  // --------------------------------------------------------
  // ControlValueAccessor interface implementation
  // --------------------------------------------------------

  writeValue(value: string): void {
    this.formControl.setValue(value);
  }
  registerOnChange(fn: any): void {
    this.localOnChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.localOnTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.changeDetectorRef.markForCheck();
    this.stateChanges.next();
  }

  // --------------------------------------------------------
  // END
  // --------------------------------------------------------

  // --------------------------------------------------------
  // MatFormField implementation
  // --------------------------------------------------------

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(event: any): void {
    if (event.type === 'click') {
    }
  }

  // --------------------------------------------------------
  // END
  // --------------------------------------------------------

  private getGroupItemsValues(groupItem: any): any[] {
    const group = groupItem[this.groupBy];
    return this.items
      .filter(
        (t) =>
          (t[this.groupBy] === group && !(t instanceof Object)) || !t.disabled
      )
      .map((t) => t[this.bindValue]);
  }

  private getSelectedItems(): any[] {
    return this.formControl.value || [];
  }

  private getGroupSelectedItemsValues(groupItem: any): any[] {
    const groupItemsValues = this.getGroupItemsValues(groupItem);
    return this.getSelectedItems().filter(
      (t) => groupItemsValues.indexOf(t) !== -1
    );
  }

  private subscribeToLanguageChange(): void {
    this.translateService.onLangChange
      .pipe(takeUntil(this.unsubscribeAll$))
      .subscribe((event) => this.setTranslations(event.lang));
  }

  private setTranslations(lang: string): void {
    this.translateService.setTranslation(lang, lang === 'no' ? no : en, true);
  }
}
